/*
 * Copyright 2026 Intuition Robotics ltd
 */
import strings from "@util/strings.json";

export type Locale = "en" | "he" | "ja";

const STRINGS: Record<Locale, Record<string, string>> = strings as Record<Locale, Record<string, string>>;

const RTL_LOCALES: Locale[] = ["he"];

/**
 * Normalize an arbitrary locale/language tag (e.g. "he-IL", "iw", "ja-JP",
 * "en-US") to one of our supported short codes, or null if unsupported.
 */
const normalizeLocale = (raw?: string | null): Locale | null => {
    if (!raw)
        return null;

    const lower = raw.toLowerCase();
    // "iw" is the legacy ISO-639 code for Hebrew still emitted by some platforms.
    if (lower.startsWith("he") || lower.startsWith("iw"))
        return "he";
    if (lower.startsWith("ja"))
        return "ja";
    if (lower.startsWith("en"))
        return "en";

    return null;
};

/**
 * Resolve the active locale: the `?lang=` query param wins (the messaging
 * backend appends the unit's language to the share link), then the browser
 * language, then English.
 */
const resolveLocale = (): Locale => {
    const param = new URLSearchParams(window.location.search).get("lang");
    const browser = typeof navigator !== "undefined" ? navigator.language : null;
    return normalizeLocale(param) || normalizeLocale(browser) || "en";
};

export const LOCALE: Locale = resolveLocale();

export const isRTL: boolean = RTL_LOCALES.includes(LOCALE);

// Reflect the locale on the document so the whole page flips direction for RTL.
if (typeof document !== "undefined") {
    document.documentElement.lang = LOCALE;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
}

/**
 * Translate a key for the active locale, falling back to English and finally
 * to the key itself so a missing string is never rendered blank.
 */
export const t = (key: string): string => {
    return (STRINGS[LOCALE] && STRINGS[LOCALE][key]) || STRINGS.en[key] || key;
};
